/**
 * Colour management primitives for the fabric pipeline.
 *
 * OWNER: fabric-digitization-specialist.
 *
 * PLATFORM-FREE. No DOM, no `node:*`. Imported by web, by the 3D layer, by the
 * asset generator and by the verification test, so that there is exactly ONE
 * implementation of "what colour is this" in the repository.
 *
 * WORKING SPACE — the whole pipeline, stated once:
 *   - Storage / interchange: sRGB IEC 61966-2-1, D65 white, 8 bits per channel,
 *     gamma-encoded. Every hex in `rules.json` is read as an sRGB triple.
 *   - Arithmetic: LINEAR light. Averaging, shading, downsampling and mip
 *     generation are done on linear values and only then re-encoded. Averaging
 *     gamma-encoded bytes is the single most common way a fabric pipeline
 *     quietly darkens a cloth; it does not happen here.
 *   - Difference: CIEDE2000 (ΔE00), computed via linear sRGB -> CIEXYZ (D65)
 *     -> CIELAB (D65, 2° observer). One function, `deltaE2000`.
 *   - Derivation: OKLab / OKLCh, because the accent colours (chalk stripe,
 *     birdseye dot, Donegal nep) are DERIVED from the managed base colour by
 *     function rather than typed in as literals. See FABRIC_PIPELINE.md §3.
 *
 * There is no colour literal anywhere in `@pt/fabric`. Every value it produces
 * is a function of `rules.json -> fabricCatalog[].colourSrgbHex` or of a
 * generated design token. `assertNoColourLiterals` in the verification test
 * proves it by scanning the source.
 */

export type Rgb8 = readonly [number, number, number];
/** Linear-light sRGB primaries, nominally 0..1 but may exceed during derivation. */
export type RgbLinear = readonly [number, number, number];
export type Lab = readonly [number, number, number];
export type OkLab = readonly [number, number, number];
/** L (0..1), C (0..~0.4), h (degrees). */
export type OkLch = readonly [number, number, number];

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

export function isHex6(value: string): boolean {
  return HEX_RE.test(value);
}

export function hexToRgb8(hex: string): Rgb8 {
  if (!isHex6(hex)) {
    throw new Error(`Not a 6-digit sRGB hex: ${JSON.stringify(hex)}`);
  }
  const n = Number.parseInt(hex.slice(1), 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}

export function rgb8ToHex(rgb: Rgb8): string {
  const c = (v: number): string => {
    const b = Math.max(0, Math.min(255, Math.round(v)));
    return b.toString(16).toUpperCase().padStart(2, '0');
  };
  return `#${c(rgb[0])}${c(rgb[1])}${c(rgb[2])}`;
}

/* ------------------------------------------------------ sRGB transfer curve */

/** sRGB electro-optical transfer function: encoded 0..1 -> linear 0..1. */
export function srgbToLinearChannel(v: number): number {
  return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
}

/** sRGB opto-electronic transfer function: linear 0..1 -> encoded 0..1. */
export function linearToSrgbChannel(v: number): number {
  return v <= 0.0031308 ? v * 12.92 : 1.055 * v ** (1 / 2.4) - 0.055;
}

/**
 * Byte -> linear, precomputed. Exact (the same function, evaluated once per code
 * value) and ~40x faster than calling `Math.pow` per channel — which matters
 * because the verification test decodes and averages about forty million pixels.
 */
export const SRGB8_TO_LINEAR: Readonly<Float64Array> = (() => {
  const t = new Float64Array(256);
  for (let i = 0; i < 256; i += 1) t[i] = srgbToLinearChannel(i / 255);
  return t;
})();

export function rgb8ToLinear(rgb: Rgb8): RgbLinear {
  const t = SRGB8_TO_LINEAR;
  return [t[rgb[0]]!, t[rgb[1]]!, t[rgb[2]]!];
}

export function linearToRgb8(lin: RgbLinear): Rgb8 {
  const q = (v: number): number => Math.round(Math.max(0, Math.min(1, linearToSrgbChannel(v))) * 255);
  return [q(lin[0]), q(lin[1]), q(lin[2])];
}

export function hexToLinear(hex: string): RgbLinear {
  return rgb8ToLinear(hexToRgb8(hex));
}

export function linearToHex(lin: RgbLinear): string {
  return rgb8ToHex(linearToRgb8(lin));
}

/**
 * In-gamut test for the sRGB working space. A tolerance of half a code value
 * (1/512) is allowed so that a value which round-trips to a legal byte is not
 * failed for floating-point dust.
 */
export function isInGamutSrgb(lin: RgbLinear, tolerance = 1 / 512): boolean {
  return lin.every((v) => v >= -tolerance && v <= 1 + tolerance);
}

/* ------------------------------------------------------------ XYZ and CIELAB */

/** sRGB (linear) -> CIEXYZ, D65. IEC 61966-2-1 matrix. */
export function linearToXyz(lin: RgbLinear): readonly [number, number, number] {
  const [r, g, b] = lin;
  return [
    0.4123907992659595 * r + 0.357584339383878 * g + 0.1804807884018343 * b,
    0.2126390058715104 * r + 0.715168678767756 * g + 0.0721923153607337 * b,
    0.0193308187155918 * r + 0.119194779794626 * g + 0.9505321522496607 * b,
  ];
}

/** D65, 2° observer. The white point of the sRGB working space. */
export const WHITE_D65: readonly [number, number, number] = [
  0.9504559270516716, 1, 1.0890577507598784,
];

export function xyzToLab(xyz: readonly [number, number, number]): Lab {
  const f = (t: number): number => (t > 216 / 24389 ? Math.cbrt(t) : (24389 / 27) * t / 116 + 16 / 116);
  const fx = f(xyz[0] / WHITE_D65[0]);
  const fy = f(xyz[1] / WHITE_D65[1]);
  const fz = f(xyz[2] / WHITE_D65[2]);
  return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
}

export function linearToLab(lin: RgbLinear): Lab {
  return xyzToLab(linearToXyz(lin));
}

export function hexToLab(hex: string): Lab {
  return linearToLab(hexToLinear(hex));
}

/* ---------------------------------------------------------------- CIEDE2000 */

/**
 * CIEDE2000 colour difference. kL = kC = kH = 1 (the graphic-arts default; the
 * house tolerance in `rules.json -> fabricAttributes.colourHonesty` is quoted
 * against exactly this). Sharma, Wu & Dalal 2005 formulation.
 */
export function deltaE2000(lab1: Lab, lab2: Lab): number {
  const [L1, a1, b1] = lab1;
  const [L2, a2, b2] = lab2;
  const rad = Math.PI / 180;
  const deg = 180 / Math.PI;

  const C1 = Math.hypot(a1, b1);
  const C2 = Math.hypot(a2, b2);
  const Cbar = (C1 + C2) / 2;
  const Cbar7 = Cbar ** 7;
  const G = 0.5 * (1 - Math.sqrt(Cbar7 / (Cbar7 + 25 ** 7)));

  const a1p = (1 + G) * a1;
  const a2p = (1 + G) * a2;
  const C1p = Math.hypot(a1p, b1);
  const C2p = Math.hypot(a2p, b2);

  const h = (ap: number, bp: number): number => {
    if (ap === 0 && bp === 0) return 0;
    const v = Math.atan2(bp, ap) * deg;
    return v >= 0 ? v : v + 360;
  };
  const h1p = h(a1p, b1);
  const h2p = h(a2p, b2);

  const dLp = L2 - L1;
  const dCp = C2p - C1p;

  let dhp: number;
  if (C1p * C2p === 0) dhp = 0;
  else if (Math.abs(h2p - h1p) <= 180) dhp = h2p - h1p;
  else if (h2p - h1p > 180) dhp = h2p - h1p - 360;
  else dhp = h2p - h1p + 360;
  const dHp = 2 * Math.sqrt(C1p * C2p) * Math.sin((dhp / 2) * rad);

  const Lbarp = (L1 + L2) / 2;
  const Cbarp = (C1p + C2p) / 2;

  let hbarp: number;
  if (C1p * C2p === 0) hbarp = h1p + h2p;
  else if (Math.abs(h1p - h2p) <= 180) hbarp = (h1p + h2p) / 2;
  else if (h1p + h2p < 360) hbarp = (h1p + h2p + 360) / 2;
  else hbarp = (h1p + h2p - 360) / 2;

  const T =
    1 -
    0.17 * Math.cos((hbarp - 30) * rad) +
    0.24 * Math.cos(2 * hbarp * rad) +
    0.32 * Math.cos((3 * hbarp + 6) * rad) -
    0.2 * Math.cos((4 * hbarp - 63) * rad);

  const dTheta = 30 * Math.exp(-(((hbarp - 275) / 25) ** 2));
  const Cbarp7 = Cbarp ** 7;
  const Rc = 2 * Math.sqrt(Cbarp7 / (Cbarp7 + 25 ** 7));
  const Sl = 1 + (0.015 * (Lbarp - 50) ** 2) / Math.sqrt(20 + (Lbarp - 50) ** 2);
  const Sc = 1 + 0.045 * Cbarp;
  const Sh = 1 + 0.015 * Cbarp * T;
  const Rt = -Math.sin(2 * dTheta * rad) * Rc;

  return Math.sqrt(
    (dLp / Sl) ** 2 + (dCp / Sc) ** 2 + (dHp / Sh) ** 2 + Rt * (dCp / Sc) * (dHp / Sh),
  );
}

export function deltaE2000Hex(a: string, b: string): number {
  return deltaE2000(hexToLab(a), hexToLab(b));
}

export function deltaE2000Linear(a: RgbLinear, b: RgbLinear): number {
  return deltaE2000(linearToLab(a), linearToLab(b));
}

/* -------------------------------------------------------------------- OKLab */

export function linearToOkLab(lin: RgbLinear): OkLab {
  const [r, g, b] = lin;
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ];
}

export function okLabToLinear(ok: OkLab): RgbLinear {
  const [L, a, b] = ok;
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}

export function okLabToOkLch(ok: OkLab): OkLch {
  const c = Math.hypot(ok[1], ok[2]);
  let hDeg = (Math.atan2(ok[2], ok[1]) * 180) / Math.PI;
  if (hDeg < 0) hDeg += 360;
  return [ok[0], c, hDeg];
}

export function okLchToOkLab(lch: OkLch): OkLab {
  const rad = (lch[2] * Math.PI) / 180;
  return [lch[0], lch[1] * Math.cos(rad), lch[1] * Math.sin(rad)];
}

/** OKLCh chroma, used by the design system's saturation law (VISUAL_SYSTEM §3.4). */
export function okChroma(hex: string): number {
  return okLabToOkLch(linearToOkLab(hexToLinear(hex)))[1];
}

/**
 * Gamut-map an OKLCh colour into sRGB by reducing chroma at constant lightness
 * and hue (binary search, 24 iterations — deterministic and sufficient at 8-bit).
 * Lightness is clamped first; a derived accent never leaves the working space.
 */
export function gamutClampOkLch(lch: OkLch): RgbLinear {
  const L = Math.max(0, Math.min(1, lch[0]));
  const direct = okLabToLinear(okLchToOkLab([L, lch[1], lch[2]]));
  if (isInGamutSrgb(direct)) return direct.map((v) => Math.max(0, Math.min(1, v))) as unknown as RgbLinear;
  let lo = 0;
  let hi = lch[1];
  for (let i = 0; i < 24; i += 1) {
    const mid = (lo + hi) / 2;
    if (isInGamutSrgb(okLabToLinear(okLchToOkLab([L, mid, lch[2]])))) lo = mid;
    else hi = mid;
  }
  const out = okLabToLinear(okLchToOkLab([L, lo, lch[2]]));
  return out.map((v) => Math.max(0, Math.min(1, v))) as unknown as RgbLinear;
}

/**
 * Derive an accent colour from a managed base colour by an OKLCh move.
 * `dL` shifts lightness, `chromaScale` multiplies chroma, `dHue` rotates hue.
 * The result is always in sRGB gamut. This is how every non-base colour in the
 * book is produced — the ONLY inputs are the managed value and three numbers,
 * so an accent can never be a colour somebody liked the look of.
 */
export function deriveAccent(
  baseHex: string,
  move: { readonly dL: number; readonly chromaScale: number; readonly dHue?: number },
): string {
  const [L, C, hDeg] = okLabToOkLch(linearToOkLab(hexToLinear(baseHex)));
  const lch: OkLch = [L + move.dL, C * move.chromaScale, (hDeg + (move.dHue ?? 0) + 360) % 360];
  return linearToHex(gamutClampOkLch(lch));
}

/** Multiply a linear triple by a scalar (a lighting/geometry term, never a hue shift). */
export function scaleLinear(lin: RgbLinear, k: number): RgbLinear {
  return [
    Math.max(0, Math.min(1, lin[0] * k)),
    Math.max(0, Math.min(1, lin[1] * k)),
    Math.max(0, Math.min(1, lin[2] * k)),
  ];
}
